import { Anchor, List, Text } from '@mantine/core';

import { LegalPage, LegalSection } from '@/components/organisms';

export function TermsPage() {
  return (
    <LegalPage
      title="Terms of Service"
      intro="These terms govern your use of Assay, a product of Goldseam AI Consulting Ltd (company number 16896682, registered in England & Wales). By creating an account you agree to these terms."
    >
      <LegalSection heading="1. About Assay">
        <Text size="sm">
          Assay is a personal CRM that helps you stay in touch with your professional contacts,
          track relationship quality, and draft personalised messages. It is provided as a
          software-as-a-service (SaaS) on a subscription basis.
        </Text>
        <Text size="sm">
          Assay is a product of <strong>Goldseam AI Consulting Ltd</strong>, registered in England &
          Wales, company number 16896682.
        </Text>
      </LegalSection>

      <LegalSection heading="2. Your account">
        <List size="sm" spacing={4}>
          <List.Item>
            You must be at least 18 years old and have the legal capacity to enter into a contract
            to use Assay.
          </List.Item>
          <List.Item>
            You are responsible for maintaining the security of your account credentials and for all
            activity that occurs under your account.
          </List.Item>
          <List.Item>
            You must provide accurate information when creating your account. Impersonation of
            others is prohibited.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="3. Acceptable use">
        <Text size="sm">
          You may use Assay only for lawful purposes and in accordance with these terms. You must
          not use Assay to:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Send unsolicited bulk messages, spam, or any communication prohibited by applicable
            anti-spam laws (including the UK Privacy and Electronic Communications Regulations 2003
            and CAN-SPAM).
          </List.Item>
          <List.Item>
            Harvest, scrape, or compile contact information from third parties without their
            consent.
          </List.Item>
          <List.Item>
            Conduct any unlawful outreach, including unsolicited direct marketing to individuals who
            have not given consent under UK GDPR.
          </List.Item>
          <List.Item>
            Impersonate any person or entity, or misrepresent your affiliation with any person or
            entity.
          </List.Item>
          <List.Item>
            Attempt to gain unauthorised access to any part of the service or its related systems.
          </List.Item>
          <List.Item>
            Use the service in any way that could damage, disable, or impair the service or
            interfere with other users.
          </List.Item>
          <List.Item>
            Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source
            code, algorithms, or trade secrets of the service.
          </List.Item>
        </List>
        <Text size="sm">
          We reserve the right to suspend or terminate accounts that breach these acceptable use
          terms, without prior notice.
        </Text>
      </LegalSection>

      <LegalSection heading="4. Data: your role as data controller">
        <Text size="sm">
          When you import or enter personal data about your contacts into Assay, you are acting as
          the <strong>data controller</strong> for that data under UK GDPR. Goldseam AI Consulting
          Ltd acts as the <strong>data processor</strong> on your behalf.
        </Text>
        <Text size="sm">By using Assay to process contact data, you warrant that:</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            You have a lawful basis under UK GDPR for processing the personal data you import (for
            example, legitimate interests in maintaining a professional network).
          </List.Item>
          <List.Item>
            You will handle any data subject requests (access, deletion, etc.) relating to your
            contacts' data promptly.
          </List.Item>
          <List.Item>
            You will not import special category data (health, religion, political views, etc.)
            unless you have explicit consent from the data subjects.
          </List.Item>
        </List>
        <Text size="sm">
          Our obligations as data processor are set out in the{' '}
          <Anchor href="/dpa" size="sm">
            Data Processing Agreement
          </Anchor>
          , which forms part of these terms. Contact data you import is sent to Anthropic's API for
          message generation - see our{' '}
          <Anchor href="/privacy" size="sm">
            Privacy Policy
          </Anchor>{' '}
          for details on all sub-processors.
        </Text>
      </LegalSection>

      <LegalSection heading="5. Subscriptions and billing">
        <List size="sm" spacing={4}>
          <List.Item>
            Assay is available on a free tier with usage limits, and on paid subscription tiers with
            higher limits and additional features. Current pricing is shown on the{' '}
            <Anchor href="/pricing" size="sm">
              pricing page
            </Anchor>
            .
          </List.Item>
          <List.Item>
            Paid subscriptions are billed in advance on a monthly or annual basis, depending on the
            plan you select.
          </List.Item>
          <List.Item>
            You may cancel your subscription at any time from your account settings. Cancellation
            takes effect at the end of the current billing period. We do not provide refunds for
            unused portions of a billing period. Nothing in this clause affects any statutory rights
            you may have under applicable consumer protection law.
          </List.Item>
          <List.Item>
            We reserve the right to change pricing with 30 days' notice. Changes will not affect
            your current billing period.
          </List.Item>
          <List.Item>
            Payments are processed by Stripe. If payment fails, we will retry over a grace period of
            7 days before downgrading your account to the free tier.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="6. Intellectual property">
        <Text size="sm">
          You retain ownership of all data you import into Assay. We do not claim any intellectual
          property rights over your contact data or the outreach messages you generate.
        </Text>
        <Text size="sm">
          The Assay application, brand, and all original software are owned by Goldseam AI
          Consulting Ltd. These terms do not grant you any rights to our intellectual property
          beyond the right to use the service as described.
        </Text>
      </LegalSection>

      <LegalSection heading="7. Disclaimers and limitation of liability">
        <Text size="sm">
          The service is provided "as is" and "as available". We make no warranty that the service
          will be uninterrupted, error-free, or meet your specific requirements.
        </Text>
        <Text size="sm">
          To the maximum extent permitted by law, Goldseam AI Consulting Ltd shall not be liable
          for:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Any indirect, incidental, special, consequential, or punitive damages.
          </List.Item>
          <List.Item>Loss of profits, revenue, data, or goodwill.</List.Item>
          <List.Item>
            Damages resulting from your reliance on AI-generated message drafts - all drafts should
            be reviewed before sending.
          </List.Item>
          <List.Item>
            Any damages exceeding the total fees you paid to us in the 12 months preceding the
            claim.
          </List.Item>
        </List>
        <Text size="sm">
          Nothing in these terms limits liability for death or personal injury caused by negligence,
          fraud, or any other liability that cannot be excluded by English law.
        </Text>
        <Text size="sm">
          We are not liable for any failure or delay in performing our obligations caused by
          circumstances beyond our reasonable control, including failures of cloud infrastructure
          providers, internet outages, cyber-attacks, natural disasters, acts of government, or
          other force majeure events. We will use reasonable endeavours to resume the service as
          soon as practicable and will notify you if a force majeure event materially affects your
          use of the service.
        </Text>
      </LegalSection>

      <LegalSection heading="8. Your indemnification obligations">
        <Text size="sm">
          You agree to indemnify, defend, and hold harmless Goldseam AI Consulting Ltd and its
          officers, directors, employees, and agents from and against any claims, liabilities,
          damages, losses, and costs (including reasonable legal fees) arising out of or in
          connection with:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Your use of the service in breach of these terms or any applicable law or regulation.
          </List.Item>
          <List.Item>
            Any outreach messages you send using the service, including any claim that such messages
            constitute spam, unlawful direct marketing, or harassment.
          </List.Item>
          <List.Item>
            Your processing of personal data in breach of UK GDPR or your obligations as a data
            controller, including any regulatory fine or third-party claim arising from such
            processing.
          </List.Item>
          <List.Item>
            Any third-party claim arising from your contact data or your use of AI-generated content
            produced by the service.
          </List.Item>
        </List>
        <Text size="sm">
          This indemnification obligation survives termination of your account and these terms.
        </Text>
      </LegalSection>

      <LegalSection heading="9. Changes to the service and these terms">
        <Text size="sm">
          We may update these terms from time to time. Material changes will be communicated by
          email or in-app notice with at least 14 days' notice before the effective date. If you do
          not agree to the updated terms, you may close your account before the effective date - see
          Section 10 (Termination) for how to do so. If you continue to use the service after the
          effective date, you will be taken to have accepted the updated terms.
        </Text>
        <Text size="sm">
          We may also modify, suspend, or discontinue the service (or any part of it) at any time,
          with reasonable notice where practicable.
        </Text>
      </LegalSection>

      <LegalSection heading="10. Termination">
        <Text size="sm">
          Either party may terminate these terms at any time. You may close your account via account
          settings or by emailing{' '}
          <Anchor href="mailto:privacy@runassay.app" size="sm">
            privacy@runassay.app
          </Anchor>
          . We may terminate your access for breach of these terms.
        </Text>
        <Text size="sm">
          On termination, your right to use the service ends immediately. We will delete your data
          in accordance with our{' '}
          <Anchor href="/privacy" size="sm">
            Privacy Policy
          </Anchor>
          .
        </Text>
      </LegalSection>

      <LegalSection heading="11. Export controls">
        <Text size="sm">
          You represent and warrant that you are not located in, or a national or resident of, any
          country subject to a UK or US government embargo or sanctions regime, and that you are not
          listed on any UK, US, or EU restricted-party list. You agree to comply with all applicable
          export control and sanctions laws in connection with your use of the service.
        </Text>
        <Text size="sm">
          Assay uses US-based sub-processors (including Anthropic, Netlify, and Trigger.dev). By
          using the service you acknowledge that your data may be processed in the United States and
          agree not to use the service in any manner that would violate applicable export
          regulations.
        </Text>
      </LegalSection>

      <LegalSection heading="12. Governing law and disputes">
        <Text size="sm">
          These terms are governed by the laws of England & Wales. Any disputes arising from or
          relating to these terms shall be subject to the exclusive jurisdiction of the courts of
          England & Wales.
        </Text>
        <Text size="sm">
          Before commencing legal proceedings, we encourage you to contact us at{' '}
          <Anchor href="mailto:help@runassay.app" size="sm">
            help@runassay.app
          </Anchor>{' '}
          to resolve disputes informally.
        </Text>
      </LegalSection>

      <Text size="sm" c="dimmed">
        Last updated: 23 March 2026 (revised). Goldseam AI Consulting Ltd, company number 16896682,
        registered in England & Wales.
      </Text>
    </LegalPage>
  );
}
